/**
 * Score entry: the two screens a scorer lives on.
 *
 * The round page is designed for one hand at a range in sunlight: one target at a time,
 * one big field pre-filled with whatever is already stored, and every save is an
 * idempotent upsert so a duplicate tap or a retried blur can never double-write. Saving
 * echoes above the keyboard — the field pulses, the phone buzzes, a line names the value
 * and the shooter it moved to — because below the field is what the on-screen keyboard
 * covers. Locking the phone mid-round costs nothing — reopening re-fetches the stored
 * values and the field lands on them, so nothing is retyped.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { api, maxFractionDigits } from "../api";
import { downscale, targetImageForm, thumbUrl } from "../images";
import { PhotoViewer } from "../photos";
import type { EntryTargetValue, EntryView, RoundStatus, RoundTarget, Shooter } from "../types";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Link,
  Meter,
  Notice,
  Spinner,
  Stack,
  Warning,
  classNames,
  formatDay,
  formatScaled,
  formatTime,
  parseScaled,
  useAsync,
  useAuth,
  useInterval,
  useLeague,
  useNoindex,
} from "../components";

const STATUS_TONE: Record<RoundStatus, "neutral" | "warn" | "info"> = {
  draft: "warn",
  open: "info",
  locked: "neutral",
};

const STATUS_LABEL: Record<RoundStatus, string> = {
  draft: "draft — hidden from the public",
  open: "open",
  locked: "locked",
};

/** Position of the first target with no stored value, 0 when every target has one. */
function firstMissing(targets: RoundTarget[], values: EntryTargetValue[]): number {
  let index = 0;
  for (const target of targets) {
    const row = values.find((value) => value.target_id === target.target_id);
    if (row === undefined || row.value_scaled === null) return index;
    index += 1;
  }
  return 0;
}

export function EntryPickPage({ slug }: { slug: string }) {
  useNoindex();
  const { user } = useAuth();
  const { league, loading, error } = useLeague(slug);
  const detail = useAsync(() => api.league(slug), [slug]);

  if (error !== null) return <ErrorNote error={error} />;
  if (loading || league === null) return <Spinner label="Loading league" />;
  if (!league.can_score) {
    return (
      <Card title="Score entry">
        <Notice>
          {user === null ? (
            <>
              Score entry needs a league account. <Link to="/login">Sign in</Link> as a
              scorer or league admin first.
            </>
          ) : (
            <>
              This account cannot enter scores for {league.name}.{" "}
              <Link to={`/${slug}`}>Open the league</Link> to watch it instead.
            </>
          )}
        </Notice>
      </Card>
    );
  }
  if (detail.error !== null) return <ErrorNote error={detail.error} />;
  if (detail.loading || detail.data === null) return <Spinner label="Loading rounds" />;

  return (
    <Card title={`Score entry — ${league.name}`}>
      {detail.data.rounds.length === 0 ? (
        <Empty
          title="No rounds to enter"
          note="This league has no rounds yet. Open the league admin page and add one on the Rounds tab."
          action={
            <Link to={`/admin/${slug}`} className="btn btn-ghost">
              League admin page
            </Link>
          }
        />
      ) : (
        <div className="entry-list">
          {detail.data.rounds.map((round) => (
            <Link
              key={round.id}
              to={`/admin/${slug}/entry/${round.id}`}
              className="entry-item"
            >
              <span className="spacer">{round.label}</span>
              <span className="muted small">{formatDay(round.plays_on)}</span>
              <Badge tone={STATUS_TONE[round.status]}>{STATUS_LABEL[round.status]}</Badge>
              {round.status === "locked" ? <Badge tone="warn">entry locked</Badge> : null}
            </Link>
          ))}
        </div>
      )}
      <p className="muted small">
        A locked round must be re-opened by a league admin before a correction, and every
        correction is audited either way.
      </p>
    </Card>
  );
}

export function EntryRoundPage({ slug, roundId }: { slug: string; roundId: string }) {
  useNoindex();
  const { league: membership, error: membershipError } = useLeague(slug);

  const [shooterId, setShooterId] = useState<string | null>(null);
  const [targetIndex, setTargetIndex] = useState(0);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [pulsing, setPulsing] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const [badNumber, setBadNumber] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [viewLead, setViewLead] = useState<string | null>(null);

  // `useAsync` gives the loading/error/reload triple. The entry view carries the round,
  // the ordered target snapshot, the active roster, the selected shooter's stored
  // values, and the round's last 20 writes; refetching it is what selecting does.
  const entryQ = useAsync(
    () =>
      membership === null
        ? Promise.resolve<EntryView | null>(null)
        : api.entry(membership.id, roundId, shooterId ?? undefined),
    [membership?.id, roundId, shooterId],
  );
  // The whole-round progress list is loaded separately and reloaded after each save and
  // every 20 s, so selecting a shooter never has to reload it.
  const progressQ = useAsync(() => api.round(slug, roundId), [slug, roundId]);
  // The shooter's photos for this round, one fetch per selected shooter; grouping by
  // target happens below at render. A private-shooter 404 (it cannot happen for a scorer,
  // who is exactly who this screen is for) stays silent: the strip is empty, the score
  // field still works, and no wall of red ever falls on scoring.
  const imagesQ = useAsync(
    () =>
      membership === null || shooterId === null
        ? Promise.resolve({ images: [] })
        : api.images({ league_id: membership.id, round_id: roundId, shooter_id: shooterId }),
    [membership?.id, roundId, shooterId],
  );

  const view = entryQ.data;
  const rows = progressQ.data?.rows ?? [];
  const scale = membership?.score_scale ?? 1;
  const locked = view?.round.status === "locked";

  const targets = view?.targets ?? [];
  const index = targets.length === 0 ? 0 : Math.min(targetIndex, targets.length - 1);
  const target: RoundTarget | null = targets.length === 0 ? null : (targets[index] ?? null);
  const shooter: Shooter | null = view?.shooter ?? null;

  // Stored values for the selected shooter, kept in a ref so a fresh fetch (the 20 s
  // refresh, a shooter switch landing) can never refill the field out from under
  // typing. Saves and clears patch it immediately so stepping back lands on the right
  // number even before the server copy arrives.
  const valuesRef = useRef<EntryTargetValue[]>([]);
  useEffect(() => {
    if (view !== null) valuesRef.current = view.values;
  }, [view]);
  const storedOf = (needle: RoundTarget | null): number | null =>
    needle === null
      ? null
      : (valuesRef.current.find((row) => row.target_id === needle.target_id)?.value_scaled ??
        null);
  const stored = storedOf(target);

  const draftKey = `${shooter?.id ?? ""}|${target?.target_id ?? ""}`;
  const filledKey = useRef<string | null>(null);
  const jumpTo = useRef<string | null>(null);
  const inFlight = useRef<Set<string>>(new Set());
  const savingRef = useRef(false);
  const pendingAdvance = useRef(false);
  const flashTimer = useRef<number | null>(null);

  // Refill the big field only when the shooter–target pair actually changes, and land a
  // jumped-to shooter on their first missing target. It reads `valuesRef` (not `view`)
  // so an auto-refresh under the same key never overwrites what is being typed.
  useEffect(() => {
    if (view === null || shooter === null || target === null) return;
    if (filledKey.current === draftKey) return;
    if (jumpTo.current === shooter.id) {
      jumpTo.current = null;
      const missing = firstMissing(view.targets, valuesRef.current);
      if (missing !== index) {
        setTargetIndex(missing);
        return;
      }
    }
    filledKey.current = draftKey;
    const existing = storedOf(target);
    setDraft(existing === null ? "" : formatScaled(existing, scale));
    setFailure(null);
    setBadNumber(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey, view, scale]);

  useEffect(
    () => () => {
      if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    },
    [],
  );
  const showFlash = useCallback((message: string) => {
    setFlash(message);
    if (flashTimer.current !== null) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlash(null), 1500);
  }, []);

  // Re-assigned every render so the stable callbacks below see fresh state through the
  // ref instead of closing over a stale render.
  const live = useRef({
    view,
    rows,
    target,
    shooter,
    draft,
    scale,
    index,
    targets,
    progressReload: progressQ.reload,
    entryReload: entryQ.reload,
    imagesReload: imagesQ.reload,
  });
  live.current = {
    view,
    rows,
    target,
    shooter,
    draft,
    scale,
    index,
    targets,
    progressReload: progressQ.reload,
    entryReload: entryQ.reload,
    imagesReload: imagesQ.reload,
  };

  const patchStored = useCallback((target_id: string, value_scaled: number | null) => {
    valuesRef.current = valuesRef.current.map((row) =>
      row.target_id === target_id ? { target_id, value_scaled } : row,
    );
  }, []);

  const nextIncomplete = useCallback((exceptId: string | null): string | null => {
    for (const row of live.current.rows) {
      if (row.shooter.id !== exceptId && row.entered < row.of) return row.shooter.id;
    }
    return null;
  }, []);

  const goToShooter = useCallback((id: string) => {
    if (id === "") return;
    jumpTo.current = id;
    setTargetIndex(0);
    setShooterId(id);
  }, []);

  // Advances one target, or to the next shooter with an incomplete card once the last
  // target is saved. Returns the display name of the shooter it switched to so the flash
  // can name the move — the progress list that would otherwise show it sits under the
  // on-screen keyboard.
  const advanceTarget = useCallback((): string | null => {
    const { targets: list, index: at, shooter: picked } = live.current;
    if (at < list.length - 1) {
      setTargetIndex(at + 1);
      return null;
    }
    const next = nextIncomplete(picked?.id ?? null);
    if (next === null) return null;
    const who = live.current.rows.find((row) => row.shooter.id === next)?.shooter ?? null;
    goToShooter(next);
    return who?.display_name ?? null;
  }, [goToShooter, nextIncomplete]);

  // Exactly one idempotent upsert per (round, shooter, target), so a blur-save racing a
  // "Save & next" cannot produce two rows: the server keys scores on that triple and `id`
  // stays undefined. No `client_id` is sent — that column is the offline queue's dedupe key
  // (Phase 4), and a readable composite of three ids does not fit its 64 characters anyway.
  const runWrite = useCallback(
    async (value_scaled: number, key: string, advance: boolean) => {
      const snapshot = live.current;
      const { view: now, target: at, shooter: who } = snapshot;
      if (now === null || at === null || who === null) return;
      inFlight.current.add(key);
      savingRef.current = true;
      setSaving(true);
      setFailure(null);
      setBadNumber(false);
      try {
        await api.putScore({
          league_id: now.league.id,
          round_id: now.round.id,
          shooter_id: who.id,
          target_id: at.target_id,
          value_scaled,
        });
        patchStored(at.target_id, value_scaled);
        // Confirm the save where the eye already is, above the keyboard: the field
        // pulses, the hand buzzes, and the flash names the value — and the shooter the
        // advance moved to, since that switch is otherwise invisible mid-entry.
        setPulsing(true);
        if (typeof navigator !== "undefined" && "vibrate" in navigator) navigator.vibrate?.(30);
        let message = `Saved ${formatScaled(value_scaled, snapshot.scale)}`;
        // A "Save & next" tapped while a blur-save was still in flight wanted the
        // advance too; honour it once the write lands.
        if (advance || pendingAdvance.current) {
          const moved = advanceTarget();
          if (moved !== null) message = `${message} → ${moved}`;
        }
        showFlash(message);
        snapshot.progressReload();
      } catch (cause: unknown) {
        // Keep the typed text exactly as entered; `detail` (locked round, out of
        // range) renders verbatim in the note under the field.
        setFailure(cause);
      } finally {
        pendingAdvance.current = false;
        inFlight.current.delete(key);
        savingRef.current = false;
        setSaving(false);
      }
    },
    [advanceTarget, patchStored, showFlash],
  );

  const apply = useCallback(
    (advance: boolean) => {
      const { view: now, target: at, shooter: who, draft: text, scale: step } = live.current;
      if (now === null || at === null || who === null) return;
      const key = `${who.id}|${at.target_id}`;
      if (inFlight.current.has(key)) {
        if (advance) pendingAdvance.current = true;
        return;
      }
      if (text.trim() === "") {
        // An empty field is not a score — not even 0. If a value is stored the field
        // was erased by hand: there is nothing to write, so "Save & next" just moves on.
        if (storedOf(at) === null) return;
        if (advance) advanceTarget();
        return;
      }
      const parsed = parseScaled(text, step);
      if (parsed === null) {
        setBadNumber(true);
        return;
      }
      // Same number already on the server: nothing to write. (0 passes this test and
      // every check below just like any other value — never a truthiness test.)
      const existing = storedOf(at);
      if (existing !== null && existing === parsed) {
        if (advance) advanceTarget();
        return;
      }
      if (savingRef.current) return;
      void runWrite(parsed, key, advance);
    },
    [advanceTarget, runWrite],
  );

  const clearCurrent = useCallback(async () => {
    const snapshot = live.current;
    const { view: now, target: at, shooter: who } = snapshot;
    if (now === null || at === null || who === null || storedOf(at) === null) return;
    if (savingRef.current || inFlight.current.has(`${who.id}|${at.target_id}`)) return;
    setFailure(null);
    try {
      await api.clearScore({
        league_id: now.league.id,
        round_id: now.round.id,
        shooter_id: who.id,
        target_id: at.target_id,
      });
      patchStored(at.target_id, null);
      setDraft("");
      showFlash("Cleared");
      snapshot.progressReload();
    } catch (cause: unknown) {
      setFailure(cause);
    }
  }, [patchStored, showFlash]);

  const toggleDq = useCallback(async () => {
    const snapshot = live.current;
    const { view: now, shooter: who, rows: progressRows } = snapshot;
    if (now === null || who === null || now.round.status === "locked") return;
    const marked = progressRows.find((row) => row.shooter.id === who.id)?.marked ?? null;
    setFailure(null);
    try {
      if (marked === null) {
        await api.mark(now.league.id, now.round.id, { shooter_id: who.id, status: "dq" });
      } else {
        await api.unmark(now.league.id, now.round.id, who.id);
      }
      snapshot.progressReload();
      snapshot.entryReload();
    } catch (cause: unknown) {
      setFailure(cause);
    }
  }, []);

  // One picture per tap. The down-scale exists so a 60 MP original does not have to
  // cross the range car-park wifi — every real check (magic bytes, the ceiling, which
  // targets exist) is the server's, and its `detail` renders verbatim in the note under
  // the field, like a refused score would. The picker gets no `capture` attribute: the
  // mobile pickers already offer the camera, and `capture` would remove the library.
  // The picker is cleared once an upload settles, never inside the change handler: a File
  // the browser is still streaming a request body from belongs to the input until the
  // request is over, and re-selecting the same file afterwards still fires a change event.
  const photoInput = useRef<HTMLInputElement | null>(null);
  const uploadPhoto = useCallback(
    async (file: File) => {
      const { view: now, target: at, shooter: who } = live.current;
      if (now === null || at === null || who === null) return;
      setUploading(true);
      setFailure(null);
      try {
        const blob = await downscale(file);
        const answer = await api.uploadImage(
          targetImageForm(
            {
              league_id: now.league.id,
              round_id: now.round.id,
              shooter_id: who.id,
              target_id: at.target_id,
            },
            blob,
            file.name === "" ? "photo.jpg" : file.name,
          ),
        );
        showFlash(answer.count === 1 ? "Photo saved — marked primary" : `Photo ${String(answer.count)} saved`);
        live.current.imagesReload();
      } catch (cause: unknown) {
        setFailure(cause);
      } finally {
        setUploading(false);
        if (photoInput.current !== null) photoInput.current.value = "";
      }
    },
    [showFlash],
  );

  // Promoting is the one gallery action the entry screen offers: the first photo wins
  // automatically, and this is how the scorer says "this one is the whole target".
  const promotePhoto = useCallback(async (imageId: string) => {
    await api.promoteImage(imageId);
    live.current.imagesReload();
  }, []);

  // A second scorer's writes surface within 20 s while the tab is visible; reloads
  // pause while a write is in flight so the stage never shifts under a save.
  const onTick = useCallback(() => {
    if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
    if (savingRef.current) return;
    live.current.progressReload();
    live.current.entryReload();
  }, []);
  useInterval(saving ? null : 20_000, onTick);

  // --- render ------------------------------------------------------------------------
  if (membershipError !== null) return <ErrorNote error={membershipError} />;
  if (membership === null) return <Spinner label="Loading round" />;
  if (!membership.can_score) {
    return (
      <Card title="Score entry">
        <Notice>
          This account cannot enter scores for {membership.name}.{" "}
          <Link to={`/${slug}`}>Open the league</Link> to watch it instead, or{" "}
          <Link to="/login">sign in</Link> as a scorer.
        </Notice>
      </Card>
    );
  }
  if (entryQ.error !== null) return <ErrorNote error={entryQ.error} />;
  if (view === null) return <Spinner label="Loading round" />;

  const everyoneDone = rows.length > 0 && rows.every((row) => row.complete);
  const marked =
    shooter === null ? null : (rows.find((row) => row.shooter.id === shooter.id)?.marked ?? null);
  const blank = draft.trim() === "";
  const canSave = shooter !== null && target !== null && !(blank && stored === null);
  const digits = maxFractionDigits(scale);
  // A decimal *string* — never float arithmetic — so the field accepts exactly the
  // precision the league's scale allows.
  const stepAttr = digits === 0 ? "1" : `0.${"0".repeat(Math.max(0, digits - 1))}1`;

  // The shooter's photos for this round arrive primary-first from the server; the strip
  // for the target on stage is the slice for it. A failed fetch means an empty strip —
  // the score field has nothing to do with the picture pipeline failing.
  const shooterImages = imagesQ.data?.images ?? [];
  const targetImages =
    target === null ? [] : shooterImages.filter((row) => row.target_id === target.target_id);

  let stageBody: ReactNode;
  if (view.shooters.length === 0) {
    stageBody = (
      <Empty
        title="No shooters on this roster yet"
        note="Open the league admin page and add shooters on the Roster tab, then come back here."
        action={
          <Link to={`/admin/${slug}`} className="btn btn-ghost">
            League admin page
          </Link>
        }
      />
    );
  } else if (targets.length === 0) {
    stageBody = (
      <Empty
        title="This round has no targets"
        note="Open the league admin page and add targets to this round on the Rounds tab."
        action={
          <Link to={`/admin/${slug}`} className="btn btn-ghost">
            League admin page
          </Link>
        }
      />
    );
  } else {
    stageBody = (
      <>
        <div className="row row-wrap">
          <span className="entry-target">
            {shooter === null ? null : (
              <>
                <strong className="entry-shooter">{shooter.display_name}</strong>
                {" · "}
              </>
            )}
            {target === null
              ? `Target ${String(index + 1)} of ${String(targets.length)}`
              : `Target ${String(index + 1)} of ${String(targets.length)} · ${target.name_snapshot}`}
          </span>
          <span className="spacer" />
          {marked === "dq" ? <Badge tone="bad">DQ</Badge> : null}
          <Button
            variant="ghost"
            onClick={() => void toggleDq()}
            disabled={shooter === null || locked}
          >
            {marked === "dq" ? "Undo DQ" : "DQ"}
          </Button>
          <Button
            variant="ghost"
            onClick={() => goToShooter(nextIncomplete(shooter?.id ?? null) ?? "")}
          >
            Next shooter
          </Button>
        </div>
        {locked ? (
          <Warning>
            Round locked — the leaderboard is frozen and entry is refused. A league admin
            must re-open the round before any correction.
          </Warning>
        ) : null}
        {shooter === null ? (
          <Notice>Choose a shooter to start — the list is below the field.</Notice>
        ) : target === null ? (
          <p className="muted">No target selected.</p>
        ) : (
          <>
            <div className="entry-facts">
              <p className="entry-max">
                {formatScaled(target.max_value_scaled, scale)} max ·{" "}
                {target.mode_snapshot === "hit_miss" ? "hit / miss" : "points"}
                {target.shots_snapshot === null
                  ? ""
                  : ` · ${String(target.shots_snapshot)} shots`}
              </p>
              <div className="photo-strip">
                {targetImages.map((image) => (
                  <button
                    key={image.id}
                    type="button"
                    className={image.is_primary ? "primary" : undefined}
                    onClick={() => {
                      setViewLead(image.id);
                      setViewing(true);
                    }}
                    aria-label={image.is_primary ? "Primary photo" : "Photo"}
                  >
                    <img src={thumbUrl(image.id)} alt="" loading="lazy" />
                    {image.is_primary ? (
                      <span className="gallery-star" aria-hidden="true">★</span>
                    ) : null}
                  </button>
                ))}
                {/* Evidence is never deleted and a locked round still accepts it: a photo
                    records what happened, it changes nothing. */}
                <label className="photo-upload" title="Add a photo of this target">
                  {uploading ? "…" : "+"}
                  <input
                    ref={photoInput}
                    type="file"
                    accept="image/*"
                    hidden
                    disabled={uploading}
                    onChange={(event) => {
                      const picked = event.target.files?.[0];
                      if (picked !== undefined) void uploadPhoto(picked);
                    }}
                  />
                </label>
              </div>
            </div>
            {viewing ? (
              <PhotoViewer
                images={targetImages}
                initialLead={viewLead}
                title={`${target.name_snapshot} · ${shooter.display_name}`}
                onClose={() => setViewing(false)}
                onPromote={promotePhoto}
              />
            ) : null}
            {flash !== null ? (
              <p className="saved-flash" role="status">
                {flash}
              </p>
            ) : null}
            {failure !== null ? (
              <ErrorNote error={failure} onDismiss={() => setFailure(null)} />
            ) : null}
            <Field
              label={`Score for ${target.name_snapshot}`}
              htmlFor="entry-score"
              error={
                badNumber
                  ? `Enter a number with at most ${String(digits)} decimal places.`
                  : undefined
              }
            >
              <Input
                id="entry-score"
                className={classNames("input-big", pulsing && "input-pulse")}
                onAnimationEnd={() => setPulsing(false)}
                inputMode="decimal"
                autoFocus
                enterKeyHint="next"
                step={stepAttr}
                value={draft}
                aria-busy={saving}
                onChange={(event) => {
                  setDraft(event.target.value);
                  setBadNumber(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    apply(true);
                  }
                }}
                onBlur={() => apply(false)}
              />
            </Field>
            <div className="row row-wrap">
              <Button
                variant="primary"
                onClick={() => apply(true)}
                disabled={!canSave}
                aria-busy={saving}
              >
                Save &amp; next
              </Button>
              <Button
                variant="ghost"
                onClick={() => apply(false)}
                disabled={!canSave}
                aria-busy={saving}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                onClick={() => setTargetIndex(Math.max(0, index - 1))}
                disabled={index === 0}
              >
                Back
              </Button>
              <Button
                variant="danger"
                onClick={() => void clearCurrent()}
                disabled={shooter === null || target === null || (stored === null && blank)}
              >
                Clear
              </Button>
            </div>
            {marked === "dq" ? (
              <p className="muted small">
                A mark labels the shooter for this round; the scores already entered keep
                counting.
              </p>
            ) : null}
            {everyoneDone ? (
              <Notice>Everyone in this round has a value for every target.</Notice>
            ) : null}
          </>
        )}
      </>
    );
  }

  return (
    <Stack>
      {/* The round context and the way to the public view share one line atop the sticky
          stage: the old banner card spent four lines and the Public link's row spent a
          fifth to leave the right half of the phone empty. */}
      <div className="entry-stage">
        <div className="row entry-meta">
          <span className="asof">
            {view.round.label} · {view.league.name} ·{" "}
            {formatDay(view.round.plays_on).replace(/, \d{4}$/, "")}
          </span>
          <span className="spacer" />
          <Link to={`/${slug}/rounds/${roundId}`} className="btn btn-ghost btn-small">
            Public view
          </Link>
        </div>
        {stageBody}
      </div>
      {view.shooters.length > 0 && targets.length > 0 ? (
        <Card title="Progress">
          {progressQ.error !== null ? (
            <ErrorNote error={progressQ.error} />
          ) : progressQ.loading && rows.length === 0 ? (
            <Spinner label="Loading progress" />
          ) : (
            <div className="progress">
              {view.shooters.map((person) => {
                const row = rows.find((entry) => entry.shooter.id === person.id);
                const of = row?.of ?? targets.length;
                const entered = row?.entered ?? 0;
                const current = shooter !== null && shooter.id === person.id;
                return (
                  <div key={person.id} className="progress-row">
                    <button
                      type="button"
                      className={classNames("entry-item", current && "entry-item-current")}
                      onClick={() => goToShooter(person.id)}
                      aria-pressed={current}
                    >
                      <span className="spacer">{person.display_name}</span>
                      {row?.marked === "dq" ? <Badge tone="bad">DQ</Badge> : null}
                      {row?.complete === true ? <Badge tone="good">complete</Badge> : null}
                    </button>
                    <span className="row">
                      <Meter value={entered} max={of} />
                      <span className="num small">
                        {String(entered)}/{String(of)}
                      </span>
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      ) : null}
      {targets.length > 0 ? (
        <Card title="Recent writes">
          {view.recent.length === 0 ? (
            <p className="muted small">Nothing entered for this round yet.</p>
          ) : (
            <div className="recent">
              {view.recent.map((recent, at) => (
                <div
                  key={`${recent.entered_at}-${recent.shooter_display_name}-${recent.target_name}-${String(at)}`}
                  className="recent-row"
                >
                  <span>
                    {recent.shooter_display_name}{" "}
                    <span className="muted">{recent.target_name}</span>
                  </span>
                  <span className="score-value num">
                    {formatScaled(recent.value_scaled, scale)}
                  </span>
                  <span className="muted small">{formatTime(recent.entered_at)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      ) : null}
    </Stack>
  );
}
